import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import themeable from '@instructure/ui-themeable';
import { translate } from '../../../constants/translated/translations';
var styles = {
  componentId: 'VnuFg',
  template: function template(theme) {
    return "\n\n.VnuFg_crZr{align-items:center;display:flex;flex:100%;position:relative}\n\n.VnuFg_crZr:focus{box-shadow:0 0 0 ".concat(theme.focusOutlineWeight || 'inherit', " ").concat(theme.focusOutlineColor || 'inherit', ";outline:none}\n\n.VnuFg_fDVa,.VnuFg_fWyE,.VnuFg_fuBh{height:100%}\n\n.VnuFg_fDVa{background-color:").concat(theme.bufferedBackgroundColor || 'inherit', "}\n\n.VnuFg_fuBh{background-color:").concat(theme.progressBackgroundColor || 'inherit', "}\n\n.VnuFg_fWyE{background-color:").concat(theme.backgroundColor || 'inherit', ";flex:1}\n\n.VnuFg_gaKF{color:").concat(theme.timestampColor || 'inherit', ";font-size:").concat(theme.timestampFontSize || 'inherit', ";font-weight:").concat(theme.timestampFontWeight || 'inherit', ";justify-content:space-between;left:0;padding:").concat(theme.timestampPadding || 'inherit', ";right:0}\n\n.VnuFg_gaKF,.VnuFg_eHzx{align-items:center;bottom:0;display:flex;position:absolute;top:0}\n\n.VnuFg_eHzx{background-color:").concat(theme.tooltipBackgroundColor || 'inherit', ";flex-direction:column;width:").concat(theme.tooltipLineWidth || 'inherit', "}\n\n.VnuFg_eHzx:focus{outline:none}\n\n.VnuFg_duqv{visibility:hidden}\n\n.VnuFg_chlC{align-items:center;display:flex;flex-direction:column;margin-top:").concat(theme.tooltipTop || 'inherit', "}\n\n.VnuFg_dkLi{background-color:").concat(theme.tooltipBackgroundColor || 'inherit', ";color:").concat(theme.tooltipColor || 'inherit', ";font-size:").concat(theme.tooltipFontSize || 'inherit', ";font-weight:").concat(theme.tooltipFontWeight || 'inherit', ";padding:").concat(theme.tooltipPadding || 'inherit', "}\n\n.VnuFg_eWjx{border-left:").concat(theme.tooltipCaretSize || 'inherit', " solid transparent;border-right:").concat(theme.tooltipCaretSize || 'inherit', " solid transparent;border-top:").concat(theme.tooltipCaretSize || 'inherit', " solid ").concat(theme.tooltipBackgroundColor || 'inherit', ";height:0;width:0}\n\n.VnuFg_fWhq{background-color:").concat(theme.tooltipBackgroundColor || 'inherit', ";height:").concat(theme.tooltipLineHeight || 'inherit', ";width:").concat(theme.tooltipLineWidth || 'inherit', "}");
  },
  'container': 'VnuFg_crZr',
  'buffered': 'VnuFg_fDVa',
  'rest': 'VnuFg_fWyE',
  'viewed': 'VnuFg_fuBh',
  'timebarContent': 'VnuFg_gaKF',
  'tooltipContainer': 'VnuFg_eHzx',
  'hidden': 'VnuFg_duqv',
  'tooltipContent': 'VnuFg_chlC',
  'tooltip': 'VnuFg_dkLi',
  'tooltipCaret': 'VnuFg_eWjx',
  'tooltipLine': 'VnuFg_fWhq'
};
import theme from './theme';

function formatTime(time) {
  if (time && typeof time === 'number' && time < Infinity) {
    return new Date(1000 * time).toISOString().substr(14, 5);
  }

  return '00:00';
}
/**
---
private: true
---
**/


var Timebar = (_dec = themeable(theme, styles), _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Timebar, _Component);

  function Timebar(props) {
    var _this;

    _classCallCheck(this, Timebar);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Timebar).call(this, props));

    _this.handleTimebarScrub = function (e) {
      var relativeCoordinate = e.pageX - e.currentTarget.getBoundingClientRect().left;
      var position = relativeCoordinate / e.currentTarget.offsetWidth;
      var timestamp = position * _this.props.duration;

      if (timestamp < 0 || timestamp > _this.props.duration) {
        // Since the tooltip is techincally inside of the container, we have to
        // manually dismiss the tooltip if the user moves the pointer outside of
        // the container.
        _this.handleTimebarLeave();
      } else {
        var tooltipCenterOffset = _this.tooltip.offsetWidth / 2;

        _this.setState({
          hoverTime: timestamp,
          tooltipPosition: Math.min(relativeCoordinate, e.currentTarget.offsetWidth) - tooltipCenterOffset
        });
      }
    };

    _this.handleTimebarLeave = function () {
      _this.setState({
        hoverTime: null,
        tooltipPosition: null
      });
    };

    _this.handleTimebarClick = function () {
      if (_this.state.hoverTime) {
        _this.props.onClick(_this.state.hoverTime);
      }
    };

    _this.state = {
      timebarHoverTime: null,
      timebarTooltipPosition: null
    };
    return _this;
  }

  _createClass(Timebar, [{
    key: "render",
    value: function render() {
      var _tooltipClasses,
          _this2 = this;

      var _this$props = this.props,
          duration = _this$props.duration,
          buffered = _this$props.buffered,
          currentTime = _this$props.currentTime,
          videoId = _this$props.videoId,
          timebarRef = _this$props.timebarRef;
      var viewedPercent = currentTime / duration * 100 || 0;
      var bufferedPercent = buffered / duration * 100 - viewedPercent || 0;
      var currentTimeText = formatTime(currentTime);
      var timebarProps = {
        className: styles.container,
        onMouseMove: this.handleTimebarScrub,
        onMouseLeave: this.handleTimebarLeave,
        onClick: this.handleTimebarClick,
        ref: timebarRef,
        tabIndex: '0',
        role: 'slider',
        'aria-label': translate('ARIA_TIMEBAR_LABEL'),
        'aria-valuemin': 0,
        'aria-valuemax': duration,
        'aria-valuenow': currentTime,
        'aria-valuetext': currentTimeText,
        'aria-controls': videoId
      };
      var tooltipClasses = (_tooltipClasses = {}, _defineProperty(_tooltipClasses, styles.tooltipContainer, true), _defineProperty(_tooltipClasses, styles.hidden, !this.state.hoverTime), _tooltipClasses);
      return React.createElement("div", timebarProps, React.createElement("div", {
        className: styles.timebarContent
      }, React.createElement("time", null, currentTimeText), React.createElement("time", null, formatTime(duration))), React.createElement("div", {
        className: styles.viewed,
        style: {
          flexBasis: "".concat(viewedPercent, "%")
        }
      }), React.createElement("div", {
        className: styles.buffered,
        style: {
          flexBasis: "".concat(bufferedPercent, "%")
        }
      }), React.createElement("div", {
        className: styles.rest
      }), React.createElement("div", {
        className: classnames(tooltipClasses),
        style: {
          left: "".concat(this.state.tooltipPosition, "px")
        },
        ref: function ref(e) {
          _this2.tooltip = e;
        },
        tabIndex: "-1"
      }, React.createElement("div", {
        className: styles.tooltipContent
      }, React.createElement("div", {
        className: styles.tooltip
      }, formatTime(this.state.hoverTime)), React.createElement("div", {
        className: styles.tooltipCaret
      }))));
    }
  }]);

  Timebar.displayName = "Timebar";
  return Timebar;
}(Component), _class2.propTypes = {
  /**
   * Id of the video element. Used to ensure
   * correct aria properties are applied.
   */
  videoId: PropTypes.string.isRequired,

  /**
   * Number of seconds that have been buffered.
   */
  buffered: PropTypes.number,

  /**
   * The current playback time in seconds.
   */
  currentTime: PropTypes.number,

  /**
   * The length of the video in seconds.
   */
  duration: PropTypes.number,

  /**
   * Function invoked when timebar is clicked.
   * Invoked with time (in seconds) at the coordinates clicked.
   */
  onClick: PropTypes.func,
  timebarRef: PropTypes.func
}, _class2.defaultProps = {
  duration: 0,
  buffered: 0,
  currentTime: 0,
  onClick: function onClick(time) {},
  timebarRef: function timebarRef(ref) {}
}, _temp)) || _class);
export default Timebar;